<template>
  <q-field
    :value="value"
    v-bind="$attrs"
    :readonly="readonly"
    color="primary-app"
    :rules="vRules"
    counter
    class="overflow-hidden"
  >
    <template #control>
      <!-- eslint-disable vue/no-v-html -->
      <div
        class="col"
        v-html="value"
      />
      <!-- eslint-enable vue/no-v-html -->
    </template>
    <q-popup-edit
      :value="true"
      content-class="column no-wrap"
      :disable="readonly"
      v-on="$listeners"
      @show="$refs.editor.focus()"
      @before-hide="$refs.editor.$refs.content.blur()"
    >
      <q-editor
        ref="editor"
        :value="value"
        :toolbar="toolbar"
        :fonts="fonts"
        :readonly="readonly"
        class="col column no-wrap"
        content-class="scroll"
        v-on="$listeners"
        @keyup.enter.stop
      >
        <template #image>
          <q-btn
            dense
            icon="fa fa-image"
            no-wrap
            unelevated
            size="sm"
          >
            <q-popup-proxy v-model="showPopupImage">
              <q-form
                class="col"
                style="min-width: 300px;"
                @submit="uploadImage"
              >
                .
                <q-card-section>
                  <lwf-picture
                    v-model="file"
                    class="q-pb-lg"
                    :rules="[$validators.required]"
                    @input="refreshFileMeta"
                  />
                  <template v-if="fileMeta">
                    <div class="row items-center q-col-gutter-sm">
                      <q-input
                        :value="fileMeta.width"
                        type="number"
                        label="Largeur"
                        suffix="px"
                        class="col"
                        @input="onResize('width', $event)"
                      />
                      <q-input
                        :value="fileMeta.height"
                        type="number"
                        label="Longueur"
                        suffix="px"
                        class="col"
                        @input="onResize('height', $event)"
                      />
                      <div class="col-auto">
                        <lwf-toggle
                          v-model="fileMeta.ratio"
                          icon="fa fa-lock"
                        >
                          <q-tooltip>
                            Préserver les proportions
                          </q-tooltip>
                        </lwf-toggle>
                      </div>
                    </div>
                  </template>
                </q-card-section>
                <dialog-component-footer />
                <q-inner-loading :showing="uploadLoading" />
              </q-form>
            </q-popup-proxy>
          </q-btn>
        </template>
        <template
          v-for="(slot, k) in vScopedSlots.editor"
          #[k]="props"
        >
          <slot
            :name="k"
            v-bind="props"
            :editor="$refs.editor"
          />
        </template>
      </q-editor>
      <q-card-actions
        align="right"
        class="col-auto"
      >
        <q-btn
          v-close-popup
          flat
          label="Valider"
        />
      </q-card-actions>
    </q-popup-edit>
    <template
      v-for="(slot, k) in $slots"
      #[k]
    >
      <slot :name="k" />
    </template>
    <template
      v-for="(slot, k) in vScopedSlots.field"
      #[k]="props"
    >
      <slot
        :name="k"
        v-bind="props"
      />
    </template>
  </q-field>
</template>

<script>
import LwfPicture from 'components/core/LwfPicture'
import DialogComponentFooter from 'components/Dialog/DialogComponentFooter'
import LwfToggle from 'components/core/LwfToggle'
import { postUpload } from '@/utils/api/utils'
import { toast } from '@/utils/ui'
import propsReadonly from '@/mixins/propsReadonly'
import validateProxyMixin from '@/mixins/validateProxyMixin'

export default {
  name: 'LwfEditor',
  components: {
    LwfToggle,
    DialogComponentFooter,
    LwfPicture
  },
  mixins: [propsReadonly, validateProxyMixin],
  inheritAttrs: false,
  props: {
    toolbar: {
      type: Array,
      default () {
        const lastItem = ['fullscreen', 'removeFormat']
        const toolbar = [
          ['undo', 'redo'],
          ['left', 'center', 'right', 'justify'],
          ['bold', 'italic', 'underline', 'link', 'image'],
          [
            {
              label: this.$q.lang.editor.formatting,
              icon: this.$q.iconSet.editor.formatting,
              list: 'no-icons',
              options: [
                'p',
                'h1',
                'h2',
                'h3',
                'h4',
                'h5',
                'h6',
                'code'
              ]
            },
            {
              label: this.$q.lang.editor.fontSize,
              icon: this.$q.iconSet.editor.fontSize,
              fixedLabel: true,
              fixedIcon: true,
              list: 'no-icons',
              options: [
                'size-1',
                'size-2',
                'size-3',
                'size-4',
                'size-5',
                'size-6',
                'size-7'
              ]
            },
            {
              label: this.$q.lang.editor.defaultFont,
              icon: this.$q.iconSet.editor.font,
              fixedIcon: true,
              list: 'no-icons',
              options: [
                'default_font',
                'arial',
                'arial_black',
                'comic_sans',
                'courier_new',
                'impact',
                'lucida_grande',
                'times_new_roman',
                'verdana'
              ]
            }
          ],
          ['quote', 'unordered', 'ordered', 'outdent', 'indent', 'hr'],
          lastItem
        ]
        if (process.env.DEV) {
          lastItem.push('viewsource')
        }
        return toolbar
      }
    },
    fonts: {
      type: Object,
      default () {
        return {
          arial: 'Arial',
          arial_black: 'Arial Black',
          comic_sans: 'Comic Sans MS',
          courier_new: 'Courier New',
          impact: 'Impact',
          lucida_grande: 'Lucida Grande',
          times_new_roman: 'Times New Roman',
          verdana: 'Verdana'
        }
      }
    },
    value: {
      type: String,
      default: ''
    },
    rules: {
      type: Array,
      default: undefined
    }
  },
  data () {
    return {
      showPopupImage: false,
      file: undefined,
      fileMeta: null,
      uploadLoading: false
    }
  },
  computed: {
    vScopedSlots () {
      const scopedSlots = {
        field: {},
        editor: {}
      }
      for (const k in this.$scopedSlots) {
        if (k.indexOf('editor-') === 0) {
          scopedSlots.editor[k] = this.$scopedSlots[k]
          continue
        }
        scopedSlots.field[k] = this.$scopedSlots[k]
      }
      return scopedSlots
    },
    vRules () {
      return [
        ...(Array.isArray(this.rules) ? this.rules : []),
        val => val.length <= 500000 || 'Maximum 500000 caractères'
      ]
    }
  },
  methods: {
    /**
     * Calcule la taille du coté opposé en préservant le ratio de l'image
     * @param file
     * @param key
     * @returns {number}
     */
    getOppositeSize (file, key) {
      const _ratio = key === 'height'
        ? file._ratio
        : 1 / file._ratio
      return Math.round(file[key] * _ratio)
    },
    onResize (key, value) {
      const _key = key === 'height'
        ? 'width'
        : 'height'
      this.fileMeta[key] = value
      if (this.fileMeta.ratio) {
        this.fileMeta[_key] = this.getOppositeSize(this.fileMeta, key)
      }
    },
    refreshFileMeta () {
      if (this.file) {
        const img = new Image()
        img.addEventListener('load', () => {
          const {
            width,
            height
          } = img
          const file = {
            width,
            height,
            ratio: true,
            _ratio: width / height
          }
          if (width > 1500) {
            file.width = 1500
            file.height = this.getOppositeSize(file, 'width')
          }
          this.fileMeta = file
        })
        img.src = this.file.base64
      }
    },
    showUploader () {
      this.file = undefined
      this.fileMeta = null
      this.showPopupImage = true
    },
    async uploadImage () {
      this.uploadLoading = true
      try {
        const file = await postUpload(this.file)
        const edit = this.$refs.editor
        edit.caret.restore()
        edit.runCmd('insertHTML', `&nbsp;<img src="${file.url}" height="${this.fileMeta.height}" width="${this.fileMeta.width}"/>&nbsp;`)
        edit.focus()
        this.showPopupImage = false
      } catch (e) {
        toast.negative(`Echec du téléchargement...`)
      }
      this.uploadLoading = false
    }
  }
}
</script>

<style scoped>

</style>
